import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { SuggestionBox } from '@components/Shared/DropDownList/DropDownList';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { SearchPostsStyles } from './SearchPosts.styled';

const SearchPosts = ({ posts, closeHandler }) => {
  /* eslint-disable react/no-unescaped-entities */
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const router = useRouter();
  const textBoxRef = useRef();

  useEffect(() => {
    if (!searchText) {
      setFilteredPosts([]);
    } else {
      const srchTxt = searchText ? searchText.toLowerCase() : null;
      if (!posts[0].index) {
        posts.forEach((p) => {
          p.index = (p.titleTag + p.slug + p.headline).toLowerCase();
        });

        posts.sort((a, b) => a.titleTag.localeCompare(b.titleTag));
      }
      setFilteredPosts(
        posts
          .filter((p) => {
            return p.index.indexOf(srchTxt) > -1 || !srchTxt;
          })
          .map((post) => {
            return {
              text: post.headline,
              value: post.slug,
            };
          })
      );
    }
  }, [searchText]);

  useEffect(() => {
    if (isOpen) {
      textBoxRef.current.focus();
    }
    setFilteredPosts([]);
    closeHandler('search', isOpen);
  }, [isOpen]);

  const searchClosed = () => {
    // Don't change it unless it is already opened.
    if (isOpen) {
      setIsOpen(false);
    }
  };

  // This is a modeled on useOnClickOutside. It is used to handle tabbing out of the SuggestionBox search box.
  const useOnFocusOut = (ref, handler) => {
    useEffect(() => {
      const listener = (event) => {
        if (event.target?.id === 'search-button') {
          return;
        }
        handler(event);
      };
      document.addEventListener('focusout', listener);
      document.addEventListener('click', listener);
      return () => {
        document.removeEventListener('focusout', listener);
        document.removeEventListener('click', listener);
      };
    }, [ref, handler]);
  };

  // Prepare to detect the SuggestionBox closed so we can display the magnifier button again.
  if (typeof document !== 'undefined') {
    // This handles detecting clicks outside of the SuggestionBox search box.
    // This handles tabbing out of the SuggestionBox search box.
    useOnFocusOut(textBoxRef, searchClosed); // eslint-disable-line react-hooks/rules-of-hooks
  }
  /* eslint-disable react/no-unescaped-entities */
  return (
    <SearchPostsStyles searchImage="/public-assets/images/magnifying-glass.svg">
      {isOpen ? (
        <div className="topic-search">
          <SuggestionBox
            automationId="search-posts-suggestion"
            label={'Search Text'}
            dropDownStyle="compact"
            items={filteredPosts}
            id="topic-search"
            onChange={(e) => {
              router.push(`/studio/${e.value}`);
              setIsOpen(false);
              pushGTMEvent('studio-category-search-submit');
            }}
            textBoxRef={textBoxRef}
            onTextChange={(e) => {
              setSearchText(e.target.value);
            }}
          ></SuggestionBox>
        </div>
      ) : (
        <div className="search-icon">
          <button
            aria-label="search posts"
            id="search-button"
            onClick={() => {
              setIsOpen((isOpen) => !isOpen);
            }}
            type="button"
          />
        </div>
      )}
    </SearchPostsStyles>
  );
};

SearchPosts.propTypes = {
  closeHandler: PropTypes.func,
  posts: PropTypes.any,
};

export default SearchPosts;
